import {
  PRODUCT_IMAGE_SURFACE_ROLES,
  type ProductImage,
  type ProductImageRole,
  type ProductImageSurface
} from '../types';

export const PRODUCT_IMAGE_CAROUSEL_LIMIT = 10;

export type ProductImageApiSurface = 'customer-dashboard' | 'website-our-food';

export const PRODUCT_IMAGE_API_SURFACES: Record<ProductImageSurface, ProductImageApiSurface> = {
  customerDashboard: 'customer-dashboard',
  websiteOurFood: 'website-our-food'
};

export const getProductImageKey = (image: ProductImage): string => image.url;

export const getProductImageSurfaceRoles = (surface: ProductImageSurface): ProductImageRole[] =>
  Object.values(PRODUCT_IMAGE_SURFACE_ROLES[surface]).flatMap(value =>
    typeof value === 'string' ? [value] : Object.values(value)
  );

export const removeProductImageRoles = (
  image: ProductImage,
  roles: ProductImageRole[]
): ProductImage => {
  const roleSortOrders = { ...image.roleSortOrders };
  roles.forEach(role => delete roleSortOrders[role]);

  return {
    ...image,
    roles: image.roles.filter(role => !roles.includes(role)),
    roleSortOrders
  };
};

export const normalizeProductImageCarousel = (
  images: ProductImage[],
  surface: ProductImageSurface
): ProductImage[] => {
  const roles = PRODUCT_IMAGE_SURFACE_ROLES[surface];
  const carouselRoles = roles.mobile ? [roles.carousel, roles.mobile.carousel] : [roles.carousel];

  return carouselRoles.reduce((currentImages, carouselRole) => {
    const orderedKeys = currentImages
      .filter(image => image.roles.includes(carouselRole))
      .sort((a, b) =>
        (a.roleSortOrders[carouselRole] ?? a.sortOrder) -
        (b.roleSortOrders[carouselRole] ?? b.sortOrder)
      )
      .map(getProductImageKey);

    return currentImages.map(image => {
      const order = orderedKeys.indexOf(getProductImageKey(image));
      return order === -1 ? image : {
        ...image,
        roleSortOrders: { ...image.roleSortOrders, [carouselRole]: order }
      };
    });
  }, images);
};

export const replaceProductImageSurfaceAssignments = (
  targetImages: ProductImage[],
  sourceImages: ProductImage[],
  surface: ProductImageSurface
): ProductImage[] => {
  const surfaceRoles = getProductImageSurfaceRoles(surface);
  const sourceByKey = new Map(sourceImages.map(image => [getProductImageKey(image), image]));

  return targetImages.map(image => {
    const source = sourceByKey.get(getProductImageKey(image));
    const roles = image.roles.filter(role => !surfaceRoles.includes(role));
    const roleSortOrders = { ...image.roleSortOrders };
    surfaceRoles.forEach(role => delete roleSortOrders[role]);

    if (!source) return { ...image, roles, roleSortOrders };

    const sourceRoles = source.roles.filter(role => surfaceRoles.includes(role));
    sourceRoles.forEach(role => {
      const order = source.roleSortOrders[role];
      if (order !== undefined) roleSortOrders[role] = order;
    });

    return { ...image, roles: [...roles, ...sourceRoles], roleSortOrders };
  });
};

export const getProductImageSurfaceState = (
  images: ProductImage[],
  surface: ProductImageSurface
) => {
  const surfaceRoles = getProductImageSurfaceRoles(surface);

  return images.map(image => ({
    key: getProductImageKey(image),
    roles: image.roles.filter(role => surfaceRoles.includes(role)).sort(),
    roleSortOrders: Object.fromEntries(surfaceRoles
      .filter(role => image.roleSortOrders[role] !== undefined)
      .map(role => [role, image.roleSortOrders[role]]))
  }));
};
